/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { sortBy } from "lodash";
import { useQueryState } from "~/hooks";
import { ResourceQuery } from "~/views/resources/types";
import { ResourceUpdateStatus } from "../types";

const STATUS_ORDER = Object.values(ResourceUpdateStatus).reduce(
  (accumulator, status, index) => ({
    ...accumulator,
    [status]: index,
  }),
  {} as Record<ResourceUpdateStatus, number>
);

const toggleStatus = (
  statuses: readonly ResourceUpdateStatus[],
  statusToToggle: ResourceUpdateStatus
): ResourceUpdateStatus[] =>
  statuses.includes(statusToToggle)
    ? statuses.filter((existingStatus) => existingStatus !== statusToToggle)
    : [...statuses, statusToToggle];

export function useUpdateStatusFilter() {
  const { queryState, updateQueryState } = useQueryState<ResourceQuery>();

  const handleStatusClick = (clickedStatus: ResourceUpdateStatus) => {
    if (
      queryState.excludeUpdateStatus &&
      clickedStatus !== ResourceUpdateStatus.Total
    ) {
      updateQueryState({
        ...queryState,
        page: "1",
        updateStatus: [clickedStatus],
        excludeUpdateStatus: undefined,
      });
      return;
    }

    const currentStatuses = queryState.updateStatus ?? [];

    const updatedStatuses =
      clickedStatus === ResourceUpdateStatus.Total
        ? undefined
        : sortBy(
            toggleStatus(currentStatuses, clickedStatus),
            (status) => STATUS_ORDER[status]
          );
    updateQueryState({
      ...queryState,
      page: "1",
      updateStatus: updatedStatuses?.length ? updatedStatuses : undefined,
      excludeUpdateStatus: undefined,
    });
  };

  return {
    activeStatuses: !queryState.excludeUpdateStatus
      ? queryState.updateStatus
      : [],
    handleStatusClick,
  };
}
